import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { Toast } from '@/shared/components/toast/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';

import { CardContainer } from '@/shared/components/card-container/card-container';
import { CardContent } from '@/shared/components/card-content/card-content';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { SelectEstado } from '@/shared/components/layout/select-estado/select-estado';
import { LayoutService } from '@/layout/service/layout.service';
import { DashboardPerfilService } from '@/features/portal/dashboard-profile/dashboard-profile.service';
import { PortalServicoService } from '@/core/api/sicom-api/portal/servico/portal-servico.service';
import { PortalServico } from '@/core/api/sicom-api/portal/servico/portal-servico.model';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';

import { ServicoDetalhe } from './componentes/servico-detalhe/servico-detalhe';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';

type CampoOrdenacao = 'servico' | 'descricao';
interface OpcaoOrdenacao {
    label: string;
    field: CampoOrdenacao;
    direction: 'asc' | 'desc';
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-servicos',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TableModule,
        TooltipModule,
        InputTextModule,
        SelectModule,
        Toast,
        ToggleSwitchModule,
        PaginatorModule,
        ConfirmDialogModule,
        CardContainer,
        CardContent,
        TableViewSwitcherComponent,
        SelectEstado,
        ServicoDetalhe,
        CanDirective,
        FiltrosBar,
        FiltroCampo
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './servicos.html',
    styleUrl: './servicos.scss'
})
/** Gestão dos serviços exibidos no portal (WEB_SERVICO), com seletor de ícone. */
export class Servicos implements OnInit {
    private readonly servicoService = inject(PortalServicoService);
    private readonly perfilService = inject(DashboardPerfilService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly permissionService = inject(PermissionService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly layoutService = inject(LayoutService);

    protected readonly roleServicosEditar = PERMISSOES.configuracoes.servicos.editar;
    protected readonly roleServicosExcluir = PERMISSOES.configuracoes.servicos.excluir;

    protected readonly viewMode = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly carregando = signal(false);

    /** Todos os serviços da UF carregada (base para filtro/ordenação client-side). */
    private readonly todos = signal<PortalServico[]>([]);

    protected readonly filtroInput = signal('');
    protected readonly filtro = signal('');
    protected readonly pageSize = signal(20);
    protected readonly currentPage = signal(0);

    protected readonly ufUsuario = signal('');
    protected readonly ufFiltro = signal('');
    protected readonly isUsuarioBR = computed(() => this.ufUsuario() === 'BR');

    /** Liga/desliga o filtro por UF (ligado = busca pela UF; desligado = todas as UFs). */
    protected readonly filtrarPorUf = signal(true);

    /** Regional não escolhe UF: o backend impõe a dele, e o select reflete isso em vez de mentir. */
    protected readonly ufTravada = computed(() => !this.permissionService.isCfm);

    protected readonly sortOpcoes: OpcaoOrdenacao[] = [
        { label: 'Nome (A→Z)', field: 'servico', direction: 'asc' },
        { label: 'Nome (Z→A)', field: 'servico', direction: 'desc' },
        { label: 'Descrição (A→Z)', field: 'descricao', direction: 'asc' },
        { label: 'Descrição (Z→A)', field: 'descricao', direction: 'desc' }
    ];
    protected readonly sortSelecionado = signal<OpcaoOrdenacao>(this.sortOpcoes[0]);

    /** Serviços após filtro de texto + ordenação (client-side). */
    private readonly filtrados = computed<PortalServico[]>(() => {
        const termo = this.filtro().trim().toLowerCase();
        const lista = termo ? this.todos().filter((s) => `${s.servico} ${s.descricao} ${s.url}`.toLowerCase().includes(termo)) : this.todos();
        const { field, direction } = this.sortSelecionado();
        const dir = direction === 'asc' ? 1 : -1;
        return [...lista].sort((a, b) => (a[field] ?? '').localeCompare(b[field] ?? '', 'pt-BR', { sensitivity: 'base' }) * dir);
    });

    protected readonly totalRecords = computed(() => this.filtrados().length);

    /** Página atual (após paginação client-side). */
    protected readonly items = computed<PortalServico[]>(() => {
        const inicio = this.currentPage() * this.pageSize();
        return this.filtrados().slice(inicio, inicio + this.pageSize());
    });

    protected readonly exibindoLabel = computed(() => {
        const total = this.totalRecords();
        const qtd = this.items().length;
        if (!total || !qtd) return '0';
        const inicio = this.currentPage() * this.pageSize() + 1;
        return `${inicio}–${Math.min(total, inicio + qtd - 1)}`;
    });

    /** UF exibida no título/descrição (a que está sendo gerenciada). */
    private readonly ufExibicao = computed(() => this.ufFiltro() || this.ufUsuario() || '—');
    protected readonly titulo = computed(() => `Plataformas e Soluções — ${this.filtrarPorUf() ? this.ufExibicao() : 'Todas as UFs'}`);
    protected readonly descricao = computed(() => (this.filtrarPorUf() ? `Cadastre e edite os serviços disponibilizados no portal da regional ${this.ufExibicao()}.` : 'Cadastre e edite os serviços disponibilizados no portal de todas as regionais.'));

    /** Modal de cadastro/edição. */
    protected readonly detalheAberto = signal(false);
    protected readonly servicoEditando = signal<PortalServico | null>(null);

    /** Token para descartar respostas fora de ordem. */
    private requisicaoAtual = 0;

    ngOnInit(): void {
        // Regional: a UF do operador já é conhecida sem ida ao servidor — o filtro inicia nela, e o perfil não a muda.
        const ufOperador = this.ufOperador();
        if (ufOperador) {
            this.ufUsuario.set(ufOperador);
            this.ufFiltro.set(ufOperador);
            this.filtrarPorUf.set(true);
        }

        this.perfilService
            .buscarPerfil()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    const perfil = Array.isArray(res) ? res[0] : res;
                    const uf = ufOperador || (perfil?.uf ?? '').trim().toUpperCase();
                    this.ufUsuario.set(uf);
                    this.ufFiltro.set(uf);
                    // Toggle "Filtrar por UF" só existe para BR → começa desligado (todas as UFs).
                    // Usuário regional não tem o toggle e continua filtrado pela própria UF.
                    this.filtrarPorUf.set(uf !== 'BR');
                    this.carregar();
                },
                error: () => this.carregar()
            });
    }

    /** UF imposta ao operador regional; `''` para o CFM, que escolhe livremente. */
    private ufOperador(): string {
        return this.ufTravada() ? (this.permissionService.ufUsuario ?? '').trim().toUpperCase() : '';
    }

    protected aplicarFiltros(): void {
        this.filtro.set(this.filtroInput());
        this.currentPage.set(0);
        this.carregar();
    }

    protected limparFiltros(): void {
        this.filtroInput.set('');
        this.filtro.set('');
        this.filtrarPorUf.set(this.ufUsuario() !== 'BR');
        this.ufFiltro.set(this.ufOperador() || this.ufUsuario());
        this.currentPage.set(0);
        this.carregar();
    }

    protected onEstadoChange(uf: string): void {
        this.ufFiltro.set((uf ?? '').toUpperCase());
    }

    /** Liga/desliga o filtro por UF. A aplicação ocorre no botão "Filtrar". */
    protected onFiltrarPorUfChange(valor: boolean): void {
        this.filtrarPorUf.set(valor);
    }

    protected onSortChange(opcao: OpcaoOrdenacao): void {
        this.sortSelecionado.set(opcao);
        this.currentPage.set(0);
    }

    protected onPageChange(event: PaginatorState): void {
        if (event.rows !== undefined) this.pageSize.set(event.rows);
        this.currentPage.set(event.page ?? 0);
    }

    protected abrirNovo(): void {
        this.servicoEditando.set(null);
        this.detalheAberto.set(true);
    }

    protected abrirDetalhe(servico: PortalServico): void {
        this.servicoEditando.set(servico);
        this.detalheAberto.set(true);
    }

    /** Após salvar no modal, recarrega a lista. */
    protected onSalvo(): void {
        this.carregar();
    }

    protected confirmarRemocao(servico: PortalServico): void {
        this.confirmationService.confirm({
            header: 'Remover serviço',
            message: `Deseja realmente remover o serviço "${servico.servico}"?`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.remover(servico)
        });
    }

    private remover(servico: PortalServico): void {
        const codigo = servico.codigo;
        if (codigo == null) {
            return;
        }
        this.servicoService
            .remover(codigo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.todos.update((lista) => lista.filter((s) => s.codigo !== codigo));
                    if (this.currentPage() > 0 && this.items().length === 0) {
                        this.currentPage.update((pagina) => pagina - 1);
                    }
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Serviço removido com sucesso.' });
                },
                error: () => undefined
            });
    }

    private carregar(): void {
        // Sem leitura de serviços: não busca (evita 403 → toast); mostra vazio.
        if (!this.permissionService.can(PERMISSOES.configuracoes.servicos.visualizar)) {
            this.todos.set([]);
            this.carregando.set(false);
            return;
        }

        const porUf = this.filtrarPorUf();
        const uf = this.ufFiltro();
        if (porUf && !uf) {
            this.todos.set([]);
            return;
        }
        this.carregando.set(true);
        const requisicao = ++this.requisicaoAtual;
        const origem = porUf ? this.servicoService.listarPorUf(uf) : this.servicoService.listarTodos();
        origem
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (lista) => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.todos.set(lista ?? []);
                    this.carregando.set(false);
                },
                error: () => {
                    if (requisicao !== this.requisicaoAtual) return;
                    this.todos.set([]);
                    this.carregando.set(false);
                }
            });
    }
}
